import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {useAuth} from "../../../context/AuthContext";
import Page, {PageMetaEntry} from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {IdentificationIcon} from "@heroicons/react/outline";
import Button from "components/Button";
import {
  CheckCircleIcon,
  EyeIcon,
  PencilIcon,
  PlusIcon,
  TicketIcon as TicketIconSolid,
  TrashIcon,
  UploadIcon,
} from "@heroicons/react/solid";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import {Ticket} from "types/Ticket";
import Dropdown from "components/Dropdown";
import DeleteTicket from '../Delete';
import DotBadge from "components/DotBadge";
import TicketStatus from "types/TicketStatus";
import TicketingSource from "types/TicketingSource";
import SearchBar from "../../../components/SearchBar";
import {SortDirection} from "../../../types/SortDirection";
import useSorting from "../../../hooks/useSorting";
import {DateTime} from "luxon";
import {useOperator} from "../../../context/OperatorContext";
import Link from "../../../components/Link";

// Helpers ----

// Map message status to badge color
const statusColors: Record<TicketStatus, number> = {
  [TicketStatus.UNPAID]: DotBadge.colors.YELLOW,
  [TicketStatus.PAID]: DotBadge.colors.BLUE,
  [TicketStatus.VALID]: DotBadge.colors.GREEN,
  [TicketStatus.EXPIRED]: DotBadge.colors.GRAY,
  [TicketStatus.REVOKED]: DotBadge.colors.RED,
}

// Component ----

function TicketList() {
  const {can} = useAuth();
  const {termFor} = useOperator();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [deletingTicket, setDeletingTicket] = useState<Ticket | null>(null);

  const {currentSort, queryString: sort, sortBy} = useSorting();
  const [search, setSearch] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data, isFetching, isLoading} = useQuery(
    ['tickets.all', {search, sort, page, perPage: 25}],
    () => ngage.tickets.filtered({search, sort, page, perPage: 25}),
    {keepPreviousData: true}
  )

  const {data: latestImport} = useQuery(
    ['imports', {category: 'tickets', perPage: 1}],
    () => ngage.imports.filtered({category: 'tickets', perPage: 1}).then(res => res?.data[0]),
    {keepPreviousData: true, enabled: can('imports:read')}
  )

  // Prefetch next and previous pages
  prefetch('tickets.all', {search, sort, page, perPage: 25}, ngage.tickets.filtered)

  // Prepare meta data
  const counts = data?.meta.counts ?? {};
  const meta: Array<PageMetaEntry> = [
    {icon: TicketIconSolid, value: (counts.all ?? 0).toLocaleString() + ' issued'},
    {icon: CheckCircleIcon, value: (counts.valid ?? 0).toLocaleString() + ' valid'},
  ]

  if(latestImport && latestImport.updated_at > DateTime.now().minus({day: 1})) {
    meta.push(
      {icon: UploadIcon, value: `Import ${latestImport.status}`, url: `/imports/${latestImport.id}`}
    )
  }

  const breadcrumbs = {
    'Commercial': '/',
    [termFor('Tickets')]: '/tickets'
  }

  const actions = (<>
    <Button icon={PlusIcon} onClick={() => navigate('/tickets/new')}>New</Button>
    {can('imports:write') && (<Button icon={UploadIcon} onClick={() => navigate('/tickets/import')}>Import</Button>)}
  </>)

  return (
    <Page title={termFor('Tickets')} meta={meta} breadcrumbs={breadcrumbs} actions={actions}>
      {/* Search and filtering */}
      <div className="mb-4 w-full grid md:grid-cols-4">
        <SearchBar isSearching={isFetching} onChange={setSearch}/>
      </div>

      {/* Data table */}
      <Table paginationData={data} setPage={setPage} isFetching={isFetching}>
        <TableHead>
          <TableRow>
            <TableHeader
              sortable
              sortDirection={currentSort.number ?? SortDirection.NONE}
              onSort={direction => sortBy('number', direction)}>
              Number
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.number ?? SortDirection.NONE}
              onSort={direction => sortBy('reference', direction)}>
              Reference
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.holder ?? SortDirection.NONE}
              onSort={direction => sortBy('holder', direction)}>
              Holder
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.type ?? SortDirection.NONE}
              onSort={direction => sortBy('type', direction)}>
              Type
            </TableHeader>
            <TableHeader
              sortable
              sortDirection={currentSort.status ?? SortDirection.NONE}
              onSort={direction => sortBy('status', direction)}>
              Status
            </TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoading}>

          {/* Empty state */}
          {data && data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={6}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <IdentificationIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No tickets</span>
                </div>
              </TableData>
            </TableRow>

            : data?.data.map((ticket, index) => (
              <TableRow key={ticket.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position='left'>
                        {[
                          {name: 'View', icon: EyeIcon, onClick: () => navigate(`/tickets/${ticket.id}`)},
                          {
                            name: 'Edit',
                            icon: PencilIcon,
                            onClick: () => navigate(`/tickets/${ticket.id}/edit`),
                            disabled: ticket.source.name !== TicketingSource.NGAGE
                          },
                          {
                            name: 'Delete',
                            icon: TrashIcon,
                            onClick: () => setDeletingTicket(ticket),
                            disabled: (ticket.source.name !== TicketingSource.NGAGE) || (ticket.status === TicketStatus.VALID)
                          }
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <IdentificationIcon className="h-5 w-5 mr-2 text-gray-500"/> {ticket.number}
                    </div>
                  </div>
                </TableData>
                <TableData>{ticket.reference ?? 'N/A'}</TableData>
                <TableData>
                  <Link to={`/audience/${ticket.holder.id}`}>
                    {ticket.holder.name}
                  </Link>
                </TableData>
                <TableData>{ticket.type.name}</TableData>
                <TableData>
                  <DotBadge color={statusColors[ticket.status]} text={ticket.status}/>
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'View', icon: EyeIcon, onClick: () => navigate(`/tickets/${ticket.id}`)},
                      {
                        name: 'Edit',
                        icon: PencilIcon,
                        onClick: () => navigate(`/tickets/${ticket.id}/edit`),
                        disabled: ticket.source.name !== TicketingSource.NGAGE
                      },
                      {
                        name: 'Delete',
                        icon: TrashIcon,
                        onClick: () => setDeletingTicket(ticket),
                        disabled: (ticket.source.name !== TicketingSource.NGAGE) || (ticket.status === TicketStatus.VALID)
                      }
                    ]}
                  </Dropdown>
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteTicket
                  ticket={deletingTicket}
                  show={ticket === deletingTicket}
                  onCancel={() => setDeletingTicket(null)}
                  onDelete={ticket => {
                    navigate('/tickets')
                    notify("success", "Ticket Deleted", `${ticket.number} has been permanently removed`)
                  }}
                  onDeleteFail={ticket => {
                    setDeletingTicket(null)
                    notify("error", "Ticket Not Deleted", `${ticket.number} could not be removed`)
                  }}
                />
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </Page>
  );
}

// Export ----

export default TicketList;